<template>
    <div>
        <div class="flex flex-wrap items-center justify-start md:justify-between py-4 gap-x-2">
            <div class="flex flex-wrap sm:items-center gap-x-2">
                <h1 class="text-xl font-semibold whitespace-nowrap text-gray-900 select-none">Maintenance Request Link Sharing</h1>

                <button class="flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition duration-300"
                        title="Return to Maintenance Requests"
                        @click.prevent="toggleList()">
                    <icon-arrow-back class="w-4 h-4"></icon-arrow-back>
                    return
                </button>
            </div>

            <div class="flex flex-wrap items-center gap-2 mt-2 sm:mt-0">
                <a :href="publicPageUrl" target="_blank"
                   class="font-bold whitespace-nowrap cursor-pointer" title="Public Maintenance Request Link">
                    <badge class="bg-indigo-100 text-indigo-600 hover:text-indigo-700 transition duration-300" text-size="text-md"> {{ publicPageUrl }}</badge>
                </a>

                <button @click.prevent="copyPublicPageUrl()">
                    <icon-copy class="w-4 h-4 text-gray-800 hover:text-indigo-600 transition duration-300"></icon-copy>
                </button>

                <secondary-button
                    @click.prevent="downloadQRCode()">
                    Download QR Code
                </secondary-button>

                <secondary-button
                    @click.prevent="openShareModal()">
                    Bulk Share
                </secondary-button>
            </div>
        </div>

        <expandable-filters-container
            :showExpandOption="false"
            :advancedFiltersExpanded="false">
            <div slot="top-panel-left">
                <div class="flex flex-wrap pb-3 pt-4 sm:py-0 sm:justify-start sm:items-center gap-4">
                    <simple-dropdown :label="portfolioFilter ? portfolioFilter.name : 'All Portfolios'"
                                     v-model="portfolioFilter"
                                     @input="fetchAssets">
                        <template slot="buttonOptions">
                            <simple-dropdown-option
                                :key="null"
                                :value="null">
                                All Portfolios
                            </simple-dropdown-option>

                            <simple-dropdown-option
                                v-for="portfolio in state.user.portfolios"
                                :key="portfolio.id"
                                :value="portfolio">
                                {{ portfolio.name | titlecase }}
                            </simple-dropdown-option>
                        </template>
                    </simple-dropdown>

                    <simple-dropdown :label="assetGroupFilter ? assetGroupFilter.name : 'All Asset Groups'"
                                     v-model="assetGroupFilter"
                                     @input="fetchAssets">
                        <template slot="buttonOptions">
                            <simple-dropdown-option
                                :key="null"
                                :value="null">
                                All Asset Groups
                            </simple-dropdown-option>

                            <simple-dropdown-option
                                v-for="assetGroup in assetGroups"
                                :key="assetGroup.id"
                                :value="assetGroup">
                                {{ assetGroup.name | titlecase }}
                            </simple-dropdown-option>
                        </template>
                    </simple-dropdown>

                    <simple-text-search-input
                        text-search-placeholder="eg. 100 Example Road"
                        text-search-label="Search Asset or Tenant"
                        v-model="searchParams.filter_text"
                        @input="fetchAssets"
                    ></simple-text-search-input>
                </div>
            </div>
        </expandable-filters-container>

        <div class="mt-4 overflow-hidden shadow sm:mx-0 rounded-lg">
            <div v-if="loading"
                 class="flex justify-center items-center w-full p-20">
                <div class="flex flex-col items-center">
                    <h2 class="text-xl font-normal text-gray-400 my-4 select-none flex align-items-center">
                        <icon-refresh class="h-8 w-8 text-indigo-600 animate-spin mr-2"/>
                        Loading Assets...
                    </h2>
                </div>
            </div>

            <div v-else-if="!loading && mutableAssets.length <= 0"
                 class="flex justify-center items-center w-full p-20">
                <div class="flex flex-col items-center">
                    <h2 class="text-xl font-normal text-gray-400 my-4 select-none">No matching assets found</h2>
                </div>
            </div>

            <table v-else-if="mutableAssets.length > 0" class="min-w-full divide-y divide-gray-300">
                <thead class="bg-white">
                    <tr class="select-none">
                        <th scope="col" class="py-3.5 pl-6 pr-3 text-left text-sm font-medium text-gray-900">
                            <simple-dropdown button-class="" justify-text="justify-start">
                                <template slot="labelHeader">
                                    <span
                                        class="flex items-center justify-start select-none rounded-md p-1 hover:bg-indigo-100 hover:text-indigo-600 transition duration-300">
                                        <icon-chevron-down class="w-4 h-4"></icon-chevron-down>
                                    </span>
                                </template>

                                <template slot="buttonOptions">
                                    <div class="py-1" role="none">
                                        <simple-dropdown-option
                                            value="select-page"
                                            @click="handlePageSelection('select-page')">
                                            Select current page
                                        </simple-dropdown-option>

                                        <simple-dropdown-option
                                            value="select-all"
                                            @click="handlePageSelection('select-all')">
                                            Select all pages
                                        </simple-dropdown-option>

                                        <simple-dropdown-option
                                            value="un-select-all"
                                            @click="handlePageSelection('un-select-all')">
                                            Un-select all <span class="font-bold">{{ selectedAssetIds.length }}</span> selected rows
                                        </simple-dropdown-option>
                                    </div>
                                </template>
                            </simple-dropdown>
                        </th>

                        <th scope="col" class="py-3.5 pl-6 pr-3 text-left text-sm font-medium text-gray-900">
                            Asset
                            <span class="inline-block lg:hidden text-gray-700">/ Address</span>
                        </th>

                        <th scope="col" class="hidden lg:table-cell py-3.5 pl-6 pr-3 text-left text-sm font-medium text-gray-900">
                            Address
                        </th>

                        <th scope="col" class="py-3.5 pl-6 pr-3 text-left text-sm font-medium text-indigo-600">
                            Tenant
                        </th>

                        <th class="hidden lg:table-cell" scope="col"></th>
                    </tr>
                </thead>

                <tbody class="divide-y divide-gray-200 bg-white">
                    <tr v-for="asset in mutableAssets"
                        class="select-none odd:bg-gray-100 hover:bg-gray-100 transition duration-300">
                        <!-- V-model components for both mobile and desktop styling -->
                        <td class="px-3 pl-7 py-4 text-sm text-gray-900 font-medium table-cell">
                            <input type="checkbox"
                                   class="focus:ring-indigo-600 h-4 w-4 text-indigo-600 border-gray-400 rounded cursor-pointer hover:border-indigo-600 transition duration-300"
                                   :value="asset.id" :key="asset.id" v-model="selectedAssetIds">
                        </td>

                        <!-- MOBILE STYLING -->
                        <td class="lg:hidden w-full py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-6">
                            <a class="text-lg sm:text-sm block cursor-pointer hover:text-indigo-600 transition duration-300" :href="`/assets/${asset.id}`"
                               target="_blank">
                                {{ asset.name }}
                            </a>
                            <dl class="font-normal lg:hidden text-lg sm:text-sm">
                                <dd class="mt-1 text-gray-700">{{ asset.full_address }}</dd>
                            </dl>
                        </td>

                        <td class="lg:hidden flex flex-col w-full py-4 pl-4 pr-3 text-sm font-medium text-indigo-600 sm:w-auto sm:max-w-none sm:pl-6 gap-2">
                            <span>{{ asset.tenant ? asset.tenant.full_name : null }}</span>

                            <button v-if="asset.tenant && Object.keys(asset.tenant).length > 0"
                                    class="whitespace-nowrap font-bold py-2 sm:py-1 px-3 rounded-md hover-pointer bg-yellow-100 text-yellow-600 hover:text-indigo-600 hover:bg-indigo-100 transition duration-300"
                                    @click.prevent="setAssetAndTenant(asset)">
                                Share Link
                                <icon-link class="w-4 h-4 inline ml-2"></icon-link>
                            </button>
                        </td>

                        <!-- DESKTOP STYLING -->
                        <td class="hidden px-3 pl-6 py-4 text-sm text-gray-900 font-medium lg:table-cell">
                            <a class="block cursor-pointer hover:text-indigo-600 transition duration-300" :href="`/assets/${asset.id}`" target="_blank">
                                {{ asset.name }}
                            </a>
                        </td>

                        <td class="hidden lg:table-cell px-3 pl-6 py-4 text-sm text-gray-900 font-medium font-normal text-sm">
                            {{ asset.full_address }}
                        </td>

                        <td class="hidden lg:table-cell font-normal text-indigo-600 px-3 pl-6 py-4 text-sm flex items-center flex-wrap">
                            {{ asset.tenant ? asset.tenant.full_name : null }}
                        </td>

                        <td class="hidden lg:table-cell py-4 pl- pr-4 text-right text-sm font-medium sm:pr-6">
                            <button v-if="asset.tenant && Object.keys(asset.tenant).length > 0"
                                    class="whitespace-nowrap font-bold py-2 text-lg sm:text-sm sm:py-1 px-3 rounded-md hover-pointer bg-yellow-100 text-yellow-600 hover:text-indigo-600 hover:bg-indigo-100 transition duration-300"
                                    @click.prevent="setAssetAndTenant(asset)">
                                Share Link
                                <icon-link class="w-4 h-4 inline ml-2"></icon-link>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <simple-pagination
            v-if="paginationData && paginationData.total > 0"
            :meta="paginationData"
            :per-page="perPage"
            :current-page="paginationData.current_page"
            padding="6"
            @page-changed="fetchAssets">
        </simple-pagination>

        <share-maintenance-request-link-modal :show="showShareModal" v-if="showShareModal" :tenant="primaryTenant" :asset="selectedAsset"
                                              :selected-asset-ids="selectedAssetIds"
                                              @toggle-modal="toggleShareModal" @clear-selection="clearSelection();">
        </share-maintenance-request-link-modal>
    </div>
</template>

<script>
import AssetGroupSearch from "../../mixins/asset-group-search";
import CopyToClipboard from "../../mixins/copy-to-clipboard";
import ExpandableFiltersContainer from "../library/ExpandableFiltersContainer";

export default {
    name: "MaintenanceRequestSharingList",

    components: {ExpandableFiltersContainer},

    mixins: [AssetGroupSearch, CopyToClipboard],

    props: {
        publicPageUrl: {
            type: String,
            required: true,
            description: "Used for displaying the link at the top of the page"
        },
    },

    data () {
        return {
            loading: true,
            mutableAssets: [],
            selectedAsset: null,
            primaryTenant: null,
            paginationData: null,

            assetGroupFilter: "",
            portfolioFilter: null,
            perPage: 15,
            searchParams: {
                filter_text: "",
                asset_group_filter: null,
                portfolio_id: null,
                page: 1,
                per_page: this.perPage
            },
            qrCode: null,
            allAssetIds: [],
            selectedAssetIds: [],
            showShareModal: false,
        };
    },

    mounted () {
        this.searchAssetGroups();
        this.fetchAssets();
    },

    methods: {
        toggleList () {
            this.$emit("toggle-list");
        },

        setSearchParams (paginationSettings = null) {
            this.$set(this.searchParams, "asset_group_filter", this.assetGroupFilter ? this.assetGroupFilter.id : null);
            this.$set(this.searchParams, "portfolio_id", this.portfolioFilter ? this.portfolioFilter.id : null);
            this.$set(this.searchParams, "page", paginationSettings?.pageNumber ? paginationSettings.pageNumber : 1);
            this.$set(this.searchParams, "per_page", paginationSettings?.perPage ? paginationSettings.perPage : this.perPage);
        },

        fetchAssets: _.debounce(function (paginationSettings = null) {
            this.loading = true;
            this.setSearchParams(paginationSettings);
            axios.get("/maintenance/requests/share/assets-list", {
                params: this.searchParams
            }).then((response) => {
                const {data, ...paginationData} = response.data;
                this.mutableAssets = data;
                this.paginationData = paginationData.meta;
                this.allAssetIds = response.data.all_asset_ids;
                // clear selection when filters change but not when page changed
                if (this.searchParams.filter_text !== "" || this.searchParams.asset_group_filter !== null || this.searchParams.portfolio_id !== null) {
                    this.clearSelection();
                }
                this.loading = false;
            }).catch(() => {
                this.loading = false;
                toastr.error("Could not fetch assets. Error: " + error);
            });
        }, 300),

        setAssetAndTenant (asset) {
            this.selectedAsset = asset;
            this.primaryTenant = asset.tenant;
            if (this.selectedAsset && !this.primaryTenant) {
                toastr.error("Asset does not have a valid primary tenant");
                return;
            }

            this.openShareModal();
        },

        openShareModal () {
            // When bulk sharing
            if (!this.primaryTenant && this.selectedAssetIds && this.selectedAssetIds.length === 0) {
                toastr.error("You must select at least one asset to share the link with");
                return;
            }

            this.toggleShareModal();
        },

        toggleShareModal () {
            this.showShareModal = !this.showShareModal;
        },

        downloadQRCode () {
            axios.get("/maintenance/requests/share/download-qr-code", {responseType: "blob"})
            .then((response) => {
                this.qrCode = response.data;

                const url = window.URL.createObjectURL(new Blob([this.qrCode]));
                const link = document.createElement("a");

                link.href = url;
                link.setAttribute("download", this.state.currentTeam.slug + "-maintenance-sharing-QR-code.png");
                document.body.appendChild(link);
                link.click();
            }).catch(() => {
                this.loading = false;
                toastr.error("Could not generate QR Code. Error: " + error);
            });
        },

        handlePageSelection (command) {
            switch (command) {
                case "select-page":
                    this.toggleSelectedOnVisibleRows(true);
                    break;
                case "select-all":
                    this.selectAll();
                    break;
                case "un-select-all":
                    this.clearSelection();
                    break;
            }
        },

        toggleSelectedOnVisibleRows (bool) {
            if (bool) {
                let self = this;
                this.mutableAssets.forEach(function (asset) {
                    if (self.isChecked(asset.id) === false) {
                        self.selectedAssetIds.push(asset.id);
                    }
                });
            } else {
                let self = this;
                this.mutableAssets.forEach(function (asset) {
                    if (self.isChecked(asset.id) === true)
                        self.selectedAssetIds.splice(self.selectedAssetIds.indexOf(asset.id), 1);
                });
            }
        },

        isChecked (assetId) {
            return this.selectedAssetIds.includes(assetId);
        },

        selectAll () {
            this.clearSelection();
            this.selectedAssetIds.push(...this.allAssetIds);
        },

        clearSelection () {
            this.selectedAssetIds.splice(0);
            this.selectedAsset = null;
            this.primaryTenant = null;
        },

        copyPublicPageUrl () {
            this.copyToClipboard(this.publicPageUrl);
            toastr.success("Link Copied!");
        },
    },
};
</script>
